/**
 * <ry-tooltip>
 *
 * Tooltip component that shows on hover/focus.
 *
 * Usage:
 * <ry-tooltip content="Save your changes">
 *   <ry-button>Save</ry-button>
 * </ry-tooltip>
 *
 * <ry-tooltip content="Delete item" position="bottom">
 *   <ry-button variant="danger">Delete</ry-button>
 * </ry-tooltip>
 *
 * JS uses data-ry-target for queries, CSS uses classes for styling.
 */

import { RyElement } from '../core/ry-element.js';

let tooltipIdCounter = 0;

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

export class RyTooltip extends RyElement {
  #showTimeout: ReturnType<typeof setTimeout> | null = null;
  #tooltipEl: HTMLElement | null = null;
  #tooltipId: string | null = null;

  setup(): void {
    this.#tooltipId = `ry-tooltip-${++tooltipIdCounter}`;

    // Create tooltip element
    this.#createTooltip();

    // Find trigger (first child element)
    const trigger = this.firstElementChild;
    if (trigger) {
      // Set ARIA
      trigger.setAttribute('aria-describedby', this.#tooltipId);

      // Mouse events
      this.on(trigger, 'mouseenter', this.#scheduleShow);
      this.on(trigger, 'mouseleave', this.#hide);

      // Focus events
      this.on(trigger, 'focusin', this.#scheduleShow);
      this.on(trigger, 'focusout', this.#hide);
    }

    // Escape to hide
    this.on(document, 'keydown', this.#handleKeydown);
  }

  #createTooltip(): void {
    // data-ry-target for JS queries, class for CSS styling
    this.#tooltipEl = document.createElement('div');
    this.#tooltipEl.id = this.#tooltipId!;
    this.#tooltipEl.setAttribute('data-ry-target', 'content');
    this.#tooltipEl.className = 'ry-tooltip__content';
    this.#tooltipEl.setAttribute('role', 'tooltip');
    this.#tooltipEl.textContent = this.getAttribute('content') ?? '';
    this.appendChild(this.#tooltipEl);
  }

  #scheduleShow = (): void => {
    // Clear any pending hide
    if (this.#showTimeout) {
      clearTimeout(this.#showTimeout);
    }

    // Delay show by 200ms
    this.#showTimeout = setTimeout(() => {
      this.#show();
    }, 200);
  };

  #show = (): void => {
    const position = (this.getAttribute('position') ?? 'top') as TooltipPosition;
    this.setAttribute('data-ry-position', position);
    this.state = 'open';
    // Positioning handled by CSS via data-ry-position attribute
  };

  #hide = (): void => {
    // Clear pending show
    if (this.#showTimeout) {
      clearTimeout(this.#showTimeout);
      this.#showTimeout = null;
    }

    this.state = 'closed';
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.state === 'open') {
      this.#hide();
    }
  };

  teardown(): void {
    if (this.#showTimeout) {
      clearTimeout(this.#showTimeout);
    }
  }
}

customElements.define('ry-tooltip', RyTooltip);
